import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { modelLabel as lookupModelLabel } from '../../services/models';

@Component({
  selector: 'app-meeting-list',
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <!-- Kullanışlı üst alan: başlık + özet istatistik + arama + yeni toplantı -->
    <div class="list-header panel-card mb-3">
      <div class="lh-top">
        <div class="lh-title">
          <h4 class="m-0"><i class="fa-regular fa-comments me-2"></i>Toplantılar</h4>
          <p class="m-0">AI destekli sanal toplantılarınızı buradan yönetin ve sürdürün.</p>
        </div>
        <a routerLink="/new" class="btn btn-primary btn-lg"><i class="fa-solid fa-plus me-2"></i>Yeni Toplantı</a>
      </div>

      <div class="lh-stats">
        <div class="stat"><span class="n">{{ meetings.length }}</span><span class="l"><i class="fa-regular fa-comments me-1"></i>Toplam</span></div>
        <div class="stat stat-active"><span class="n">{{ countActive }}</span><span class="l"><span class="dot"></span>Devam Eden</span></div>
        <div class="stat stat-done"><span class="n">{{ countCompleted }}</span><span class="l"><i class="fa-solid fa-check me-1"></i>Tamamlanan</span></div>

        <div class="lh-search">
          <i class="fa-solid fa-magnifying-glass"></i>
          <input [(ngModel)]="search" placeholder="Konuya göre ara...">
          @if (search) { <button type="button" (click)="search = ''" title="Temizle"><i class="fa-solid fa-xmark"></i></button> }
        </div>
      </div>
    </div>

    @if (loading) {
      <div class="panel-card text-center text-muted py-5">
        <i class="fa-solid fa-spinner fa-spin me-2"></i> Yükleniyor...
      </div>
    } @else if (!meetings.length) {
      <div class="panel-card text-center text-muted py-5">
        Henüz toplantı yok. <a routerLink="/new">İlk toplantınızı oluşturun</a>.
      </div>
    } @else if (!filteredMeetings.length) {
      <div class="panel-card text-center text-muted py-5">
        "<strong>{{ search }}</strong>" aramasına uygun toplantı bulunamadı.
      </div>
    } @else {
      <div class="table-responsive">
        <table class="table table-hover align-middle">
          <thead>
            <tr>
              <th>#</th><th>Konu</th><th>Tür</th><th>Durum</th><th>Bütçe</th><th>Derinlik</th><th>Tarih</th>
            </tr>
          </thead>
          <tbody>
            @for (m of filteredMeetings; track m.id) {
              <tr (click)="open(m)">
                <td>{{ m.id }}</td>
                <td class="topic">{{ topicText(m.topic) }}</td>
                <td>{{ m.meetingType || '-' }}</td>
                <td><span class="badge" [class]="'badge ' + statusClass(m.status)">{{ statusLabel(m.status) }}</span></td>
                <td>{{ m.creditsUsed | number:'1.0-1' }} / {{ m.creditBudget | number }} kredi</td>
                <td>{{ modelLabel(m.model) }}</td>
                <td>{{ m.createdAt | date:'dd.MM.yyyy HH:mm' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    }
  `,
  styles: [`
    .topic { max-width: 380px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

    .list-header { padding: 0; overflow: hidden; }
    .lh-top {
      display: flex; justify-content: space-between; align-items: center; gap: 16px;
      padding: 20px 22px;
      background: linear-gradient(120deg, var(--brand-deep) 0%, var(--brand-alt) 100%);
      color: #fff;
    }
    .lh-title h4 { font-weight: 700; }
    .lh-title p { margin-top: 4px !important; font-size: 13px; opacity: .8; }
    .lh-top .btn-primary { --bs-btn-bg: rgba(255,255,255,.16); --bs-btn-border-color: rgba(255,255,255,.25); --bs-btn-hover-bg: rgba(255,255,255,.28); --bs-btn-hover-border-color: rgba(255,255,255,.4); white-space: nowrap; }

    .lh-stats { display: flex; align-items: stretch; gap: 10px; padding: 14px 22px; flex-wrap: wrap; }
    .stat {
      display: flex; flex-direction: column; justify-content: center;
      min-width: 120px; padding: 10px 16px; border-radius: 10px; background: #f6f4fb; border: 1px solid #ece7f6;
    }
    .stat .n { font-size: 22px; font-weight: 700; line-height: 1; color: var(--brand-deep); }
    .stat .l { font-size: 12px; color: #6c757d; margin-top: 5px; display: flex; align-items: center; }
    .stat-active .n { color: #198754; }
    .stat-active .dot { width: 8px; height: 8px; border-radius: 50%; background: #198754; margin-right: 5px; }
    .stat-done .n { color: var(--brand-accent); }

    .lh-search {
      display: flex; align-items: center; gap: 8px; flex: 1; min-width: 200px;
      margin-left: auto; padding: 0 14px; border-radius: 10px; background: #fff; border: 1px solid #dee2e6;
    }
    .lh-search i { color: #adb5bd; }
    .lh-search input { border: 0; outline: 0; padding: 10px 0; flex: 1; font-size: 14px; background: transparent; }
    .lh-search button { border: 0; background: none; color: #adb5bd; cursor: pointer; }
    .lh-search button:hover { color: #6c757d; }

    @media (max-width: 640px) {
      .lh-top { flex-direction: column; align-items: flex-start; }
      .lh-search { margin-left: 0; }
    }
  `]
})
export class MeetingListComponent implements OnInit {
  meetings: any[] = [];
  loading = true;
  search = '';

  constructor(private api: ApiService, private router: Router) {}

  ngOnInit() {
    this.api.PostData('meeting/list', {}, (resp: any) => {
      this.meetings = resp || [];
      this.loading = false;
    }, () => this.loading = false);
  }

  get filteredMeetings(): any[] {
    const q = this.search.trim().toLocaleLowerCase('tr');
    if (!q) return this.meetings;
    return this.meetings.filter(m =>
      this.topicText(m.topic).toLocaleLowerCase('tr').includes(q) ||
      (m.meetingType || '').toLocaleLowerCase('tr').includes(q));
  }

  get countActive(): number {
    return this.meetings.filter(m => ['Active', 'WrapUp', 'Closing'].includes(m.status)).length;
  }

  get countCompleted(): number {
    return this.meetings.filter(m => m.status === 'Completed').length;
  }

  // Konu HTML olabilir; listede düz metin göster.
  topicText(html: string): string {
    return (html || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  }

  modelLabel(id: string): string {
    return lookupModelLabel(id);
  }

  open(m: any) {
    this.router.navigate(['/meeting', m.id]);
  }

  statusLabel(s: string): string {
    const map: any = { Setup: 'Hazırlık', Active: 'Devam Ediyor', WrapUp: 'Toparlama', Closing: 'Kapanış', Completed: 'Tamamlandı', Draft: 'Taslak' };
    return map[s] || s;
  }

  statusClass(s: string): string {
    const map: any = { Setup: 'text-bg-secondary', Active: 'text-bg-success', WrapUp: 'text-bg-warning', Closing: 'text-bg-warning', Completed: 'text-bg-dark', Draft: 'text-bg-light' };
    return map[s] || 'text-bg-secondary';
  }
}
